import React, { useState } from 'react';
import { Group, Text, Button, ActionIcon, Tooltip, Popover, TextInput } from '@mantine/core';
import { IconUsers, IconLink, IconSettings, IconWand, IconArrowLeft, IconPencil } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

export default function PlanningHeader({
  name,
  onShare,
  onShowPeople,
  onOpenSettings,
  onOpenSidebar,
  onBack,
  isMobile,
  canEdit = false,
  onRename,
}) {
  const { t } = useTranslation('common');
  const [renameOpen, setRenameOpen] = useState(false);
  const [draftName, setDraftName] = useState(name || '');

  const openRename = () => {
    setDraftName(name || '');
    setRenameOpen(true);
  };

  const commitRename = async () => {
    const next = String(draftName || '').trim();
    setRenameOpen(false);
    if (!next || next === name) return;
    try { await onRename?.(next); } catch {}
  };
  return (
    <div className="planning-header">
      {isMobile ? (
        <>
          <Group justify="space-between" align="center">
            <ActionIcon variant="subtle" onClick={onBack} title={t('planning.header.back')}>
              <IconArrowLeft size={18} />
            </ActionIcon>
            <Group gap={6} align="center">
              <Text fw={700}>{name || t('planning.default_name')}</Text>
              {canEdit && (
                <Popover opened={renameOpen} onChange={setRenameOpen} position="bottom" withArrow shadow="md">
                  <Popover.Target>
                    <Tooltip label={t('common.edit')}>
                      <ActionIcon variant="subtle" onClick={openRename} aria-label={t('common.edit')}>
                        <IconPencil size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Popover.Target>
                  <Popover.Dropdown>
                    <Group align="flex-end" gap="xs" wrap="nowrap">
                      <TextInput
                        placeholder={t('planning.form.name_label')}
                        value={draftName}
                        onChange={(e) => setDraftName(e.currentTarget.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenameOpen(false); }}
                      />
                      <Button size="xs" onClick={commitRename}>{t('common.save')}</Button>
                    </Group>
                  </Popover.Dropdown>
                </Popover>
              )}
            </Group>
            {/* spacer to keep title visually centered */}
            <div style={{ width: 32, height: 32 }} />
          </Group>
          {/* One-line actions: Share, People, Settings */}
          <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            <Button fullWidth variant="light" leftSection={<IconLink size={16} />} onClick={onShare}>{t('planning.header.share')}</Button>
            <Button fullWidth variant="light" leftSection={<IconUsers size={16} />} onClick={onShowPeople}>{t('planning.header.people')}</Button>
            <Button fullWidth variant="outline" leftSection={<IconSettings size={16} />} onClick={onOpenSettings}>{t('planning.header.settings')}</Button>
          </div>
          {/* Full-width Optimize below */}
          <div style={{ marginTop: 8 }}>
            <Button fullWidth leftSection={<IconWand size={16} />} onClick={onOpenSidebar}>{t('optimization.optimize')}</Button>
          </div>
        </>
      ) : (
        <Group justify="space-between" align="center">
          <Group align="center" gap="sm">
            <ActionIcon variant="subtle" onClick={onBack} title={t('planning.header.back')}>
              <IconArrowLeft size={18} />
            </ActionIcon>
            <Group gap={6} align="center">
              <Text fw={700}>{name || t('planning.default_name')}</Text>
              {canEdit && (
                <Popover opened={renameOpen} onChange={setRenameOpen} position="bottom-start" withArrow shadow="md">
                  <Popover.Target>
                    <Tooltip label={t('common.edit')}>
                      <ActionIcon variant="subtle" onClick={openRename} aria-label={t('common.edit')}>
                        <IconPencil size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Popover.Target>
                  <Popover.Dropdown>
                    <Group align="flex-end" gap="xs" wrap="nowrap">
                      <TextInput
                        placeholder={t('planning.form.name_label')}
                        value={draftName}
                        onChange={(e) => setDraftName(e.currentTarget.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenameOpen(false); }}
                      />
                      <Button size="xs" onClick={commitRename}>{t('common.save')}</Button>
                    </Group>
                  </Popover.Dropdown>
                </Popover>
              )}
            </Group>
          </Group>
          <Group gap="xs" wrap="nowrap" style={{ overflowX: 'auto' }}>
            <Button variant="light" leftSection={<IconLink size={16} />} onClick={onShare}>{t('planning.header.share')}</Button>
            <Button variant="light" leftSection={<IconUsers size={16} />} onClick={onShowPeople}>{t('planning.header.people')}</Button>
            <Button variant="outline" leftSection={<IconSettings size={16} />} onClick={onOpenSettings}>{t('planning.header.settings')}</Button>
          </Group>
        </Group>
      )}
    </div>
  );
}
